import { AppError } from '@repo/error';
import { errorMessage, t } from '@repo/i18n';
import { useLocale } from '@repo/i18n/react';
import { ReportType, RiskLevel } from '@repo/shared/schema';
import { BookmarkIcon, UserRoundIcon, UsersIcon } from 'lucide-react';
import { useState } from 'react';
import { useLocation, useSearchParams } from 'react-router';
import { toast } from 'sonner';
import { ListToolbar } from '@/components/raven/list-toolbar';
import { dateLabel, OLink, Pagination, QueryState, RiskBadge } from '@/components/raven/shared';
import { trpc } from '@/providers/trpc';

export const ClientsPage = () => {
  useLocale();

  const location = useLocation();
  const [searchParams] = useSearchParams();
  const bookmarked = location.pathname.endsWith('/bookmarks');
  const [search, setSearch] = useState('');
  const [reportType, setReportType] = useState(searchParams.get('reportType') ?? 'ALL');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(1);
  const query = trpc.workspace.listClients.useQuery({
    search,
    sortOrder,
    reportType:
      reportType === ReportType.KYC || reportType === ReportType.KYB ? reportType : undefined,
    bookmarked,
    page,
    perPage: 10,
    days: Number(searchParams.get('days')) || undefined,
  });
  const utils = trpc.useUtils();
  const bookmark = trpc.workspace.updateBookmark.useMutation({
    onSuccess: () => {
      void utils.workspace.invalidate();
    },
    onError: (cause) => toast.error(errorMessage(AppError.parse(cause))),
  });
  return (
    <main className="raven-page">
      <span className="raven-label">
        {bookmarked ? t('auth.savedClients') : t('workspace.clients')}
      </span>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h1>{bookmarked ? t('workspace.bookmarks') : t('auth.allClients')}</h1>
        <OLink className="rounded bg-[#222e9c] px-4 py-3 text-sm text-white" to="/reports/create">
          {t('auth.addNewClient')}{' '}
        </OLink>
      </div>
      <p className="mb-8 text-sm">{t('auth.viewAndManageTheClientsYouAreResponsibleFor')}</p>
      <div>
        <ListToolbar
          onOrder={(value) => {
            setSortOrder(value);
            setPage(1);
          }}
          onSearch={(value) => {
            setSearch(value);
            setPage(1);
          }}
          order={sortOrder}
          search={search}
          sortBy="createdAt"
        >
          <label className="flex flex-col gap-2 text-xs">
            {t('reports.clientType')}{' '}
            <select
              aria-label={t('auth.clientType')}
              className="raven-select"
              onChange={(event) => {
                setReportType(event.target.value);
                setPage(1);
              }}
              value={reportType}
            >
              <option value="ALL">{t('reports.allTypes')}</option>
              <option>{t('reports.kyc')}</option>
              <option>{t('reports.kyb')}</option>
            </select>
          </label>
        </ListToolbar>
        <QueryState
          error={query.isError}
          loading={query.isLoading}
          retry={() => void query.refetch()}
        />
        <div className="mb-4">
          <h2>{t('auth.myClients')}</h2>
          <p className="raven-muted mt-2">
            {query.data?.total ?? 0} {t('auth.clientsFound')}
          </p>
        </div>
        <div className="raven-table-frame overflow-auto">
          <table className="raven-table">
            <thead>
              <tr>
                <th>{t('reports.clientName')}</th>
                <th>{t('workspace.lastReview')}</th>
                <th>{t('reports.clientType')}</th>
                <th>{t('reports.riskLevel')}</th>
                <th>{t('reports.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {query.data?.data.map((client) => (
                <tr key={client._id}>
                  <td>
                    <div className="flex items-center gap-2">
                      <span aria-hidden="true" className="raven-avatar" />
                      <span className="raven-client-name">
                        {client.name}
                        <small className="raven-muted mt-1 block font-sans">
                          {dateLabel(client.acceptedAt)}
                        </small>
                      </span>
                    </div>
                  </td>
                  <td>{dateLabel(client.updatedAt ?? client.acceptedAt, true)}</td>
                  <td>
                    <span className="raven-risk">
                      {client.reportType === ReportType.KYB ? (
                        <>
                          <UsersIcon className="me-1 inline size-3" />
                          {t('reports.business')}{' '}
                        </>
                      ) : (
                        <>
                          <UserRoundIcon className="me-1 inline size-3" />
                          {t('reports.individual')}{' '}
                        </>
                      )}
                    </span>
                  </td>
                  <td>
                    <RiskBadge level={client.riskLevel ?? RiskLevel.UNASSESSED} />
                  </td>
                  <td>
                    <div className="flex items-center gap-4">
                      <OLink label={t('auth.viewClient')} to={`/clients/${client._id}`}>
                        <span title={t('auth.viewClient')}>
                          <UserRoundIcon className="size-4" />
                        </span>
                      </OLink>
                      <button
                        aria-label={
                          client.bookmarked ? t('auth.removeBookmark') : t('auth.bookmarkClient')
                        }
                        aria-pressed={client.bookmarked}
                        disabled={bookmark.isPending}
                        onClick={() =>
                          bookmark.mutate({
                            clientId: client._id,
                            data: { bookmarked: !client.bookmarked },
                          })
                        }
                        type="button"
                      >
                        <BookmarkIcon
                          className="size-4"
                          fill={client.bookmarked ? 'currentColor' : 'none'}
                        />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {query.data?.data.length === 0 && (
                <tr>
                  <td colSpan={5}>
                    {bookmarked
                      ? t('auth.noBookmarkedClients')
                      : t('auth.noAcceptedClientsMatchYourSearch')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="raven-table-footer flex flex-wrap items-center justify-between">
          <span className="raven-muted">
            {t('reports.showing')} {query.data?.total ? (page - 1) * 10 + 1 : 0}–
            {Math.min(page * 10, query.data?.total ?? 0)} {t('reports.of')} {query.data?.total ?? 0}{' '}
            {t('auth.clients')}{' '}
          </span>
          <Pagination onChange={setPage} page={page} totalPages={query.data?.totalPages ?? 1} />
        </div>
      </div>
    </main>
  );
};
